import React, { useRef, useEffect, useCallback, useState } from "react";
import { cx } from "@jugl-web/utils";
import { formatSecondsToCompactDuration } from "@jugl-web/utils/date-time/formatSecondsToCompactDuration";
import {
  InteractiveContainer,
  LoadingSpinner,
  DelayedRender,
} from "@jugl-web/ui-kit";
import { useMediaPlayer } from "@web-src/modules/media/providers/MediaPlayerProvider/useMediaPlayer";
import { useUnmount } from "react-use";
import { ReactComponent as PlayIcon } from "./assets/play.svg";
import { ReactComponent as PauseIcon } from "./assets/pause.svg";

const getDurationStorageKey = (src: string) => `audio:duration:${src}`;

type VoiceMessageAudioPlayerVariant = "incoming" | "outgoing" | "compact";

interface VoiceMessageAudioPlayerProps {
  audioSrc: string;
  waveform: number[];
  variant: VoiceMessageAudioPlayerVariant;
  duration?: number;
}

const VARIANT_STYLES: Record<
  VoiceMessageAudioPlayerVariant,
  {
    idleBar: string;
    progressBar: string;
    timeClassName: string;
    buttonIdle: string;
    buttonPlaying: string;
  }
> = {
  incoming: {
    idleBar: "#A4A5AA",
    progressBar: "#1A75D2",
    timeClassName: "text-grey",
    buttonIdle: "bg-primary text text-white hover:bg-primary-600",
    buttonPlaying: "bg-gradients-g3 text-white hover:bg-[#9D4AD7]",
  },
  outgoing: {
    idleBar: "#FFFFFF",
    progressBar: "#90C9F9",
    timeClassName: "text-white",
    buttonIdle: "bg-white text-primary hover:bg-grey-200",
    buttonPlaying: "bg-white text-gradients-g3 hover:bg-grey-200",
  },
  compact: {
    idleBar: "#A4A5AA",
    progressBar: "#1A75D2",
    timeClassName: "font-secondary leading-[130%] text-grey",
    buttonIdle: "bg-grey-200 text-[#B021F3] hover:bg-grey-400",
    buttonPlaying: "bg-grey-200 text-[#B021F3] hover:bg-grey-400",
  },
};

const roundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
  ctx.fill();
};

export const VoiceMessageAudioPlayer: React.FC<
  VoiceMessageAudioPlayerProps
> = ({ audioSrc, waveform, variant, duration: defaultDuration = 0 }) => {
  const {
    currentAudio,
    playAudio,
    pauseAudio,
    resumeAudio,
    setCurrentTime,
    stopAudio,
  } = useMediaPlayer();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const [storedDuration, setStoredDuration] = useState(() => {
    if (defaultDuration) {
      return defaultDuration;
    }
    const storedValue = sessionStorage.getItem(getDurationStorageKey(audioSrc));
    if (storedValue && !Number.isNaN(Number(storedValue))) {
      return Number(storedValue);
    }
    return 0;
  });
  const [isDragging, setIsDragging] = useState(false);
  const isCompact = variant === "compact";
  const variantStyles = VARIANT_STYLES[variant];

  const isThisAudioLoaded = currentAudio?.src === audioSrc;
  const isThisAudioPlaying = isThisAudioLoaded && currentAudio.isPlaying;
  const playingDuration = isThisAudioLoaded ? currentAudio.currentTime : 0;
  const audioDuration = isThisAudioLoaded
    ? currentAudio.duration
    : defaultDuration || storedDuration;

  // Store duration in session storage when it becomes available
  useEffect(() => {
    if (isThisAudioLoaded && currentAudio.duration > 0 && !defaultDuration) {
      sessionStorage.setItem(
        getDurationStorageKey(audioSrc),
        currentAudio.duration.toString()
      );
      setStoredDuration(currentAudio.duration);
    }
  }, [isThisAudioLoaded, currentAudio?.duration, audioSrc, defaultDuration]);

  const drawWaveform = useCallback(
    (actualTime: number) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (ctx && canvas) {
        const dpr = window.devicePixelRatio || 1;
        const canvasWidth = canvas.offsetWidth;
        const canvasHeight = canvas.offsetHeight;

        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;

        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = false;

        const segmentWidth = canvasWidth / waveform.length;

        // `audioDuration` is 0 on the failure path (`setAudioDuration`'s catch
        // returns without updating state) — dividing by it yields Infinity and
        // paints every bar as fully played
        const safeDuration =
          Number.isFinite(audioDuration) && audioDuration > 0
            ? audioDuration
            : 0;
        const progressX = safeDuration
          ? (Math.min(Math.max(actualTime, 0), safeDuration) / safeDuration) *
            canvasWidth
          : 0;

        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        waveform.forEach((value, index) => {
          const barHeight = Math.max(3, value * canvasHeight * 0.8);
          const x = index * segmentWidth;
          const barWidth = segmentWidth * 0.3;
          const barX = x + (segmentWidth - barWidth) / 2;
          const barY = canvasHeight - barHeight;
          const progressInBar = Math.max(
            0,
            Math.min(barWidth, progressX - barX)
          );

          ctx.fillStyle = variantStyles.idleBar;
          roundedRect(ctx, barX, barY, barWidth, barHeight, barWidth / 2);
          if (progressInBar > 0) {
            ctx.fillStyle = variantStyles.progressBar;
            roundedRect(
              ctx,
              barX,
              barY,
              progressInBar,
              barHeight,
              barWidth / 2
            );
          }
        });
      }
    },
    [waveform, variantStyles, audioDuration]
  );

  // Update waveform when currentTime changes
  useEffect(() => {
    if (!isDragging && isThisAudioLoaded) {
      drawWaveform(playingDuration);
    }
  }, [playingDuration, isThisAudioLoaded, isDragging, drawWaveform]);

  // Initial drawing
  useEffect(() => {
    drawWaveform(isThisAudioLoaded ? playingDuration : 0);
  }, [drawWaveform, isThisAudioLoaded, playingDuration]);

  const handleCanvasClick = useCallback(
    (e: MouseEvent | React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;

      if (!canvas || !isThisAudioLoaded) return;
      if (!Number.isFinite(audioDuration) || audioDuration <= 0) return;

      const rect = canvas.getBoundingClientRect();

      if (rect.width <= 0) return;

      // The mousemove listener is bound to `document`, not the canvas, so
      // dragging past either edge produces an out-of-range offset. Clamp the
      // ratio rather than the resulting time so the locally painted waveform
      // stays in sync with what the provider is told
      const ratio = Math.min(
        Math.max((e.clientX - rect.left) / rect.width, 0),
        1
      );
      const clickTime = ratio * audioDuration;

      setCurrentTime(clickTime);
      drawWaveform(clickTime);
    },
    [drawWaveform, audioDuration, setCurrentTime, isThisAudioLoaded]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent | React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (isThisAudioLoaded) {
        e.preventDefault();
        setIsDragging(true);
        handleCanvasClick(e);
      }
    },
    [handleCanvasClick, isThisAudioLoaded]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging && isThisAudioLoaded) {
        handleCanvasClick(e);
      }
    },
    [handleCanvasClick, isDragging, isThisAudioLoaded]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isThisAudioLoaded) return undefined;

    canvas.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleMouseMove, handleMouseDown, handleMouseUp, isThisAudioLoaded]);

  const togglePlayPause = useCallback(() => {
    if (isThisAudioPlaying) {
      pauseAudio();
    } else if (isThisAudioLoaded) {
      resumeAudio();
    } else {
      if (currentAudio) {
        stopAudio();
      }
      playAudio(audioSrc, "Voice Message", audioDuration || undefined);
    }
  }, [
    isThisAudioPlaying,
    isThisAudioLoaded,
    pauseAudio,
    resumeAudio,
    currentAudio,
    stopAudio,
    playAudio,
    audioSrc,
    audioDuration,
  ]);

  const buttonClassNames = isThisAudioPlaying
    ? variantStyles.buttonPlaying
    : variantStyles.buttonIdle;

  // Cleanup on unmount
  useUnmount(() => {
    if (currentAudio?.src === audioSrc) {
      stopAudio();
    }
  });

  const $time = (
    <span className={cx("text-sm", variantStyles.timeClassName)} ref={timeRef}>
      {formatSecondsToCompactDuration(
        isThisAudioLoaded ? playingDuration : audioDuration,
        { padLeadingUnit: true }
      )}
    </span>
  );

  return (
    <div
      className={cx("flex items-center", isCompact ? "gap-3" : "gap-2.5 p-1")}
    >
      <InteractiveContainer
        onClick={togglePlayPause}
        className={cx(
          "flex shrink-0 items-center justify-center rounded-full bg-primary transition-colors",
          isCompact ? "h-9 w-9" : "h-11 w-11",
          buttonClassNames
        )}
      >
        <DelayedRender
          isLoading={isThisAudioPlaying && !!currentAudio?.isLoading}
          loadingContent={
            <LoadingSpinner className={isCompact ? "h-4 w-4" : "h-5 w-5"} />
          }
        >
          {isThisAudioPlaying ? <PauseIcon /> : <PlayIcon />}
        </DelayedRender>
      </InteractiveContainer>
      {isCompact ? (
        <>
          <canvas
            ref={canvasRef}
            className={cx(
              "h-8 min-w-0 flex-1",
              isThisAudioLoaded && "cursor-pointer"
            )}
          />
          {$time}
        </>
      ) : (
        <div className="flex w-full flex-col gap-1">
          <canvas
            ref={canvasRef}
            className={cx("h-10 w-full", isThisAudioLoaded && "cursor-pointer")}
          />
          {$time}
        </div>
      )}
    </div>
  );
};
